import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStorefront, type Currency } from '@/hooks/useStorefront';

type RateRow = { base_currency: string; quote_currency: string; rate: number | null };

export interface UseExchangeRateResult {
  rates: Partial<Record<Currency, number>> | null;
  getRate: (c: Currency) => number | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Loads exchange rates once and caches them for 12h. React-Query is called
 * unconditionally (Rules of Hooks) but the network fetch is gated behind
 * `enabled: storefront !== 'ch'` so CH performs no request and stays
 * byte-identical.
 */
export function useExchangeRate(): UseExchangeRateResult {
  const { storefront } = useStorefront();
  const enabled = storefront !== 'ch';

  const query = useQuery({
    queryKey: ['exchange_rates'],
    enabled,
    staleTime: 12 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exchange_rates')
        .select('base_currency,quote_currency,rate')
        .eq('base_currency', 'CHF');
      if (error) throw error;
      const map: Partial<Record<Currency, number>> = {};
      for (const row of (data as unknown as RateRow[]) ?? []) {
        const r = typeof row.rate === 'number' ? row.rate : Number(row.rate);
        if (r > 0 && isFinite(r)) {
          map[row.quote_currency as Currency] = r;
        }
      }
      return map;
    },
  });

  const rates = enabled ? (query.data ?? null) : null;
  return {
    rates,
    getRate: (c: Currency) => (rates && rates[c] ? rates[c]! : null),
    isLoading: enabled ? query.isLoading : false,
    isError: enabled ? query.isError : false,
  };
}